import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  Building2,
  Calendar,
  CheckCircle2,
  FolderKanban,
  Lightbulb,
  Loader2,
  Package,
  Target,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { useCompanyList } from "@/modules/companies/hooks/useCompanies";
import { useProductList } from "@/modules/products/hooks/useProducts";
import { useProjectList } from "@/modules/projects/hooks/useProjects";
import { useUserList } from "@/modules/users/hooks/useUsers";
import { useMeetingList } from "@/modules/meetings/hooks/useMeetings";
import { useDecisionList } from "@/modules/decisions/hooks/useDecisions";
import { useIdeaList } from "@/modules/ideas/hooks/useIdeas";
import { useMilestoneList } from "@/modules/milestones/hooks/useMilestones";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { PhaseStagePicker } from "@/components/shared/PhaseStagePicker";
import { useCreateTask } from "../hooks/useTasks";
import type { TaskPriority, TaskStatus, TaskListItem } from "../types";

type LockedFields = {
  companyId?: boolean;
  productId?: boolean;
  projectId?: boolean;
  meetingId?: boolean;
  decisionId?: boolean;
  ideaId?: boolean;
  milestoneId?: boolean;
};

type PrefillContext = {
  companyId?: string | null;
  productId?: string | null;
  projectId?: string | null;
  meetingId?: string | null;
  meetingTitle?: string | null;
  decisionId?: string | null;
  decisionTitle?: string | null;
  ideaId?: string | null;
  ideaTitle?: string | null;
  milestoneId?: string | null;
  milestoneTitle?: string | null;
  locked?: LockedFields;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (code: string) => void;
  prefill?: PrefillContext;
  /** When set, the drawer opens in "Duplicate" mode: sticky context is copied
   *  from this record while date/status fields are cleared. Submitting creates
   *  a brand-new task. */
  duplicateFrom?: TaskListItem;
};

export function NewTaskDrawer({
  isOpen,
  onClose,
  onCreated,
  prefill,
  duplicateFrom,
}: Props) {
  const { user } = useAuth();
  const createTask = useCreateTask();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<TaskPriority>("MEDIUM");
  const [status, setStatus] = useState<TaskStatus>("OPEN");
  const [startDate, setStartDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [estimatedHours, setEstimatedHours] = useState("");
  const [selectedCompanyId, setSelectedCompanyId] = useState(
    prefill?.companyId ?? "",
  );
  const [selectedProductId, setSelectedProductId] = useState(
    prefill?.productId ?? "",
  );
  const [selectedProjectId, setSelectedProjectId] = useState(
    prefill?.projectId ?? "",
  );
  const [selectedMeetingId, setSelectedMeetingId] = useState(
    prefill?.meetingId ?? "",
  );
  const [selectedDecisionId, setSelectedDecisionId] = useState(
    prefill?.decisionId ?? "",
  );
  const [selectedIdeaId, setSelectedIdeaId] = useState(prefill?.ideaId ?? "");
  const [selectedMilestoneId, setSelectedMilestoneId] = useState(
    prefill?.milestoneId ?? "",
  );
  const [selectedPhaseId, setSelectedPhaseId] = useState<string | null>(null);
  const [selectedStageId, setSelectedStageId] = useState<string | null>(null);
  const [assignedToId, setAssignedToId] = useState(user?.id ?? "");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedCompanyId(prefill?.companyId ?? "");
      setSelectedProductId(prefill?.productId ?? "");
      setSelectedProjectId(prefill?.projectId ?? "");
      setSelectedMeetingId(prefill?.meetingId ?? "");
      setSelectedDecisionId(prefill?.decisionId ?? "");
      setSelectedIdeaId(prefill?.ideaId ?? "");
      setSelectedMilestoneId(prefill?.milestoneId ?? "");
    }
  }, [
    isOpen,
    prefill?.companyId,
    prefill?.productId,
    prefill?.projectId,
    prefill?.meetingId,
    prefill?.decisionId,
    prefill?.ideaId,
    prefill?.milestoneId,
  ]);

  // Duplicate mode: copy sticky context, clear date/status fields.
  useEffect(() => {
    if (!isOpen || !duplicateFrom) return;
    const d = duplicateFrom;
    setTitle(d.title ?? "");
    setDescription(d.description ?? "");
    setPriority(d.priority);
    setStatus("OPEN");
    setEstimatedHours(d.estimatedHours != null ? String(d.estimatedHours) : "");
    setSelectedCompanyId(d.companyId ?? "");
    setSelectedProductId(d.productId ?? "");
    setSelectedProjectId(d.projectId ?? "");
    setSelectedPhaseId(d.phaseId ?? null);
    setSelectedStageId(d.stageId ?? null);
    setSelectedMeetingId(d.meetingId ?? "");
    setSelectedDecisionId(d.decisionId ?? "");
    setSelectedIdeaId(d.ideaId ?? "");
    setSelectedMilestoneId(d.milestoneId ?? "");
    setAssignedToId(d.assignedToId ?? "");
    // Cleared — new dates/status for the new task:
    setStartDate("");
    setDueDate("");
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, duplicateFrom]);

  useEffect(() => {
    if (user?.id && !assignedToId) setAssignedToId(user.id);
  }, [user?.id, assignedToId]);

  const { data: companiesResult } = useCompanyList({
    page: 1,
    limit: 100,
    operationalOnly: true,
  });
  const companies = companiesResult?.data ?? [];

  const { data: productsResult } = useProductList({
    page: 1,
    limit: 100,
    companyId: selectedCompanyId || undefined,
  });
  const products = productsResult?.data ?? [];

  const { data: projectsResult } = useProjectList({
    page: 1,
    limit: 100,
    companyId: selectedCompanyId || undefined,
    productId: selectedProductId || undefined,
  });
  const projects = projectsResult?.data ?? [];

  const { data: usersResult } = useUserList({ page: 1, limit: 100 });
  const allUsers = usersResult?.data ?? [];

  const { data: meetingsResult } = useMeetingList({ page: 1, limit: 100 });
  const allMeetings = meetingsResult?.data ?? [];
  const { data: decisionsResult } = useDecisionList({ page: 1, limit: 100 });
  const allDecisions = decisionsResult?.data ?? [];
  const { data: ideasResult } = useIdeaList({ page: 1, limit: 100 });
  const allIdeas = ideasResult?.data ?? [];
  const { data: milestonesResult } = useMilestoneList({ page: 1, limit: 100 });
  const allMilestones = milestonesResult?.data ?? [];

  function reset() {
    setTitle("");
    setDescription("");
    setPriority("MEDIUM");
    setStatus("OPEN");
    setStartDate("");
    setDueDate("");
    setEstimatedHours("");
    setSelectedCompanyId(prefill?.companyId ?? "");
    setSelectedProductId(prefill?.productId ?? "");
    setSelectedProjectId(prefill?.projectId ?? "");
    setSelectedMeetingId(prefill?.meetingId ?? "");
    setSelectedDecisionId(prefill?.decisionId ?? "");
    setSelectedIdeaId(prefill?.ideaId ?? "");
    setSelectedMilestoneId(prefill?.milestoneId ?? "");
    setSelectedPhaseId(null);
    setSelectedStageId(null);
    setAssignedToId(user?.id ?? "");
    setError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    setError(null);
    try {
      const res = await createTask.mutateAsync({
        title: title.trim(),
        description: description.trim() || null,
        status,
        priority,
        startDate: startDate ? new Date(startDate).toISOString() : null,
        dueDate: dueDate ? new Date(dueDate).toISOString() : null,
        estimatedHours: estimatedHours ? Number(estimatedHours) : null,
        companyId: selectedCompanyId || null,
        productId: selectedProductId || null,
        projectId: selectedProjectId || null,
        phaseId: selectedPhaseId,
        stageId: selectedStageId,
        meetingId: selectedMeetingId || null,
        decisionId: selectedDecisionId || null,
        ideaId: selectedIdeaId || null,
        milestoneId: selectedMilestoneId || null,
        assignedToId: assignedToId || null,
      });
      onCreated?.(res.task.code);
      reset();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create task");
    }
  }

  if (!isOpen) return null;

  const lockCompany = !!prefill?.locked?.companyId && !!prefill?.companyId;
  const lockProduct = !!prefill?.locked?.productId && !!prefill?.productId;
  const lockProject = !!prefill?.locked?.projectId && !!prefill?.projectId;
  const lockMeeting = !!prefill?.locked?.meetingId && !!prefill?.meetingId;
  const lockDecision =
    !!prefill?.locked?.decisionId && !!prefill?.decisionId;
  const lockIdea = !!prefill?.locked?.ideaId && !!prefill?.ideaId;
  const lockMilestone =
    !!prefill?.locked?.milestoneId && !!prefill?.milestoneId;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[9998] bg-black/30"
        onClick={onClose}
      />
      <div
        className="fixed right-0 top-0 z-[9999] flex h-full w-[500px] flex-col border-l bg-white shadow-xl"
        style={{ borderColor: "hsl(210,30%,85%)" }}
      >
        <div
          className="flex items-center justify-between border-b px-6 py-4"
          style={{ borderColor: "hsl(210,30%,85%)" }}
        >
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              {duplicateFrom ? "Duplicate Task" : "New Task"}
            </h2>
            {duplicateFrom && (
              <p className="mt-0.5 text-xs text-muted-foreground">
                Duplicating from {duplicateFrom.code} — {duplicateFrom.title}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex flex-1 flex-col overflow-y-auto"
        >
          <div className="flex-1 space-y-6 p-6">
            <h3 className="text-sm font-semibold text-foreground">
              Task Details
            </h3>

            <div className="space-y-2">
              <Label htmlFor="tsk-title">
                Title <span className="text-destructive">*</span>
              </Label>
              <Input
                id="tsk-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter task title"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="tsk-desc">Description</Label>
              <textarea
                id="tsk-desc"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the task (optional)"
                className="flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              />
            </div>

            <h3 className="text-sm font-semibold text-foreground">
              Priority &amp; Status
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="tsk-priority">Priority</Label>
                <Select
                  id="tsk-priority"
                  value={priority}
                  onChange={(e) =>
                    setPriority(e.target.value as TaskPriority)
                  }
                >
                  <option value="CRITICAL">Critical</option>
                  <option value="HIGH">High</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="LOW">Low</option>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="tsk-status">Status</Label>
                <Select
                  id="tsk-status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as TaskStatus)}
                >
                  <option value="OPEN">Open</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="IN_REVIEW">In Review</option>
                  <option value="CANCELLED">Cancelled</option>
                </Select>
              </div>
            </div>

            <h3 className="text-sm font-semibold text-foreground">Dates</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="tsk-start">Start Date</Label>
                <Input
                  id="tsk-start"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tsk-due">Due Date</Label>
                <Input
                  id="tsk-due"
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tsk-est">Est. Hours</Label>
                <Input
                  id="tsk-est"
                  type="number"
                  step="0.25"
                  min="0"
                  value={estimatedHours}
                  onChange={(e) => setEstimatedHours(e.target.value)}
                />
              </div>
            </div>

            <h3 className="text-sm font-semibold text-foreground">
              Association
            </h3>

            <div className="space-y-2">
              <Label htmlFor="tsk-company">Company</Label>
              <div className="relative">
                <Building2
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="tsk-company"
                  value={selectedCompanyId}
                  disabled={lockCompany}
                  onChange={(e) => {
                    setSelectedCompanyId(e.target.value);
                    setSelectedProductId("");
                    setSelectedProjectId("");
                  }}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm transition-colors disabled:bg-muted disabled:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">Select a company...</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tsk-product">Product</Label>
              <div className="relative">
                <Package
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="tsk-product"
                  value={selectedProductId}
                  disabled={lockProduct}
                  onChange={(e) => {
                    setSelectedProductId(e.target.value);
                    setSelectedProjectId("");
                  }}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm transition-colors disabled:bg-muted disabled:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No product</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tsk-project">Project</Label>
              <div className="relative">
                <FolderKanban
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="tsk-project"
                  value={selectedProjectId}
                  disabled={lockProject}
                  onChange={(e) => {
                    setSelectedProjectId(e.target.value);
                    setSelectedPhaseId(null);
                    setSelectedStageId(null);
                  }}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm transition-colors disabled:bg-muted disabled:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No project</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <PhaseStagePicker
              projectId={selectedProjectId || null}
              phaseId={selectedPhaseId}
              stageId={selectedStageId}
              onPhaseChange={setSelectedPhaseId}
              onStageChange={setSelectedStageId}
            />

            <div className="space-y-2">
              <Label htmlFor="tsk-meeting">Meeting</Label>
              <div className="relative">
                <Calendar
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="tsk-meeting"
                  value={selectedMeetingId}
                  disabled={lockMeeting}
                  onChange={(e) => setSelectedMeetingId(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm transition-colors disabled:bg-muted disabled:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No meeting</option>
                  {allMeetings.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.code} — {m.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tsk-decision">Decision</Label>
              <div className="relative">
                <CheckCircle2
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="tsk-decision"
                  value={selectedDecisionId}
                  disabled={lockDecision}
                  onChange={(e) => setSelectedDecisionId(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm transition-colors disabled:bg-muted disabled:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No decision</option>
                  {allDecisions.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.code} — {d.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tsk-idea">Parent Idea</Label>
              <div className="relative">
                <Lightbulb
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="tsk-idea"
                  value={selectedIdeaId}
                  disabled={lockIdea}
                  onChange={(e) => setSelectedIdeaId(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm transition-colors disabled:bg-muted disabled:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No parent idea</option>
                  {allIdeas.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.code} — {i.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tsk-milestone">Milestone</Label>
              <div className="relative">
                <Target
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="tsk-milestone"
                  value={selectedMilestoneId}
                  disabled={lockMilestone}
                  onChange={(e) => setSelectedMilestoneId(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm transition-colors disabled:bg-muted disabled:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No milestone</option>
                  {allMilestones.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.code} — {m.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <h3 className="text-sm font-semibold text-foreground">
              Assignment
            </h3>
            <div className="space-y-2">
              <Label htmlFor="tsk-assignee">Assigned To</Label>
              <select
                id="tsk-assignee"
                value={assignedToId}
                onChange={(e) => setAssignedToId(e.target.value)}
                className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              >
                <option value="">Unassigned</option>
                {allUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>

            {error && (
              <div
                className="rounded-md border px-3 py-2 text-sm"
                style={{
                  borderColor: "hsl(0,70%,80%)",
                  background: "hsl(0,70%,96%)",
                  color: "hsl(0,70%,35%)",
                }}
              >
                {error}
              </div>
            )}
          </div>

          <div
            className="flex items-center justify-end gap-3 border-t px-6 py-4"
            style={{ borderColor: "hsl(210,30%,85%)" }}
          >
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!title.trim() || createTask.isPending}
            >
              {createTask.isPending && (
                <Loader2
                  className="mr-1 h-4 w-4 animate-spin"
                  strokeWidth={1.5}
                />
              )}
              Create Task
            </Button>
          </div>
        </form>
      </div>
    </>,
    document.body,
  );
}
